import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
  CFormSelect,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCalendar, cilColumns, cilStar, cilChartLine, cilDollar, cilBadge } from '@coreui/icons'
import React, { useState } from 'react'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import { resolvePlayerImageUrl } from 'src/utils/imageUrl'

const SEGMENTS = [
  { key: 'summary', label: 'Summary' },
  { key: 'batting', label: 'Batting' },
  { key: 'bowling', label: 'Bowling' },
  { key: 'fielding', label: 'Fielding' },
]

const SUMMARY_COLUMNS = [
  { label: 'Round', filter: false, sorter: false, key: 'round' },
  { label: 'Fixtures', filter: false, sorter: false, key: 'fixtures_count' },
  { label: 'Fantasy Points', filter: false, sorter: false, key: 'sum_fp' },
  { label: 'Runs', filter: false, sorter: false, key: 'sum_rs' },
  { label: 'Wickets', filter: false, sorter: false, key: 'sum_wks' },
  { label: 'Catches', filter: false, sorter: false, key: 'sum_cs' },
  { label: 'Value at Round', filter: false, sorter: false, key: 'value_at_round' },
]

const BATTING_COLUMNS = [
  { label: 'Round', filter: false, sorter: false, key: 'round' },
  { label: 'Fixtures', filter: false, sorter: false, key: 'fixtures_count' },
  { label: 'Runs', filter: false, sorter: false, key: 'sum_rs' },
  { label: 'Fours', filter: false, sorter: false, key: 'sum_fours' },
  { label: 'Sixes', filter: false, sorter: false, key: 'sum_sixes' },
  { label: 'Value at Round', filter: false, sorter: false, key: 'value_at_round' },
]

const BOWLING_COLUMNS = [
  { label: 'Round', filter: false, sorter: false, key: 'round' },
  { label: 'Fixtures', filter: false, sorter: false, key: 'fixtures_count' },
  { label: 'Overs', filter: false, sorter: false, key: 'sum_overs' },
  { label: 'Maidens', filter: false, sorter: false, key: 'sum_mdns' },
  { label: 'Wickets', filter: false, sorter: false, key: 'sum_wks' },
  { label: 'Value at Round', filter: false, sorter: false, key: 'value_at_round' },
]

const FIELDING_COLUMNS = [
  { label: 'Round', filter: false, sorter: false, key: 'round' },
  { label: 'Fixtures', filter: false, sorter: false, key: 'fixtures_count' },
  { label: 'Catches', filter: false, sorter: false, key: 'sum_cs' },
  { label: 'Caught & Bowled/WK', filter: false, sorter: false, key: 'sum_cwks' },
  { label: 'Stumpings', filter: false, sorter: false, key: 'sum_sts' },
  { label: 'Run Outs (D/A)', filter: false, sorter: false, key: 'run_outs' },
  { label: 'Value at Round', filter: false, sorter: false, key: 'value_at_round' },
]

const SEGMENT_COLUMNS = {
  summary: SUMMARY_COLUMNS,
  batting: BATTING_COLUMNS,
  bowling: BOWLING_COLUMNS,
  fielding: FIELDING_COLUMNS,
}

const RoundPlayerStatsTab = (props) => {
  const [statsData, setStatsData] = useState([])
  const [player, setPlayer] = useState(null)
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(false)
  const [selectedRound, setSelectedRound] = useState('')
  const [segment, setSegment] = useState('summary')
  const [hiddenColumns, setHiddenColumns] = useState({})

  const handlePlayer = (e) => {
    const playerId = e.target.value
    if (!playerId) {
      setStatsData([])
      setPlayer(null)
      setSummary({})
      return
    }
    setLoading(true)
    PlayerService.playerStatsByRound(playerId)
      .then((res) => {
        if (res.success) {
          setStatsData(res.data || [])
          setPlayer(res.player || null)
          setSummary(res.summary || {})
          setSelectedRound('')
        }
        setLoading(false)
      })
      .catch(() => {
        ToastComponent('Failed to load round stats for this player', 'error')
        setLoading(false)
      })
  }

  const toggleColumn = (key) => {
    setHiddenColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const rows = selectedRound
    ? statsData.filter((r) => String(r.round) === String(selectedRound))
    : statsData
  const columns = SEGMENT_COLUMNS[segment].filter((col) => !hiddenColumns[col.key])
  const imgSrc = player ? resolvePlayerImageUrl(player) : null

  return (
    <>
      <CCard className="mb-4">
        <CCardBody>
          <CRow className="g-3 align-items-end mb-3">
            <CCol md={3}>
              <CFormSelect
                aria-label="Select player"
                name="player"
                onChange={handlePlayer}
                id="player"
              >
                <option value="">Select Player</option>
                {props.playerList &&
                  props.playerList.map((item, key) => (
                    <option value={item?.id} key={key}>
                      {item?.full_name}
                    </option>
                  ))}
              </CFormSelect>
            </CCol>
            <CCol md={3}>
              <CFormSelect value={selectedRound} onChange={(e) => setSelectedRound(e.target.value)}>
                <option value="">All Rounds</option>
                {statsData.map((r) => (
                  <option value={r.round} key={r.round}>
                    Round {r.round}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md="auto">
              <CButton
                color="light"
                className="d-inline-flex p-0 rounded-2 overflow-hidden border"
                style={{ gap: 0 }}
              >
                {SEGMENTS.map((s) => (
                  <span
                    key={s.key}
                    role="button"
                    onClick={() => setSegment(s.key)}
                    className={`px-3 py-2 small ${
                      segment === s.key ? 'bg-primary-subtle text-primary fw-semibold' : 'text-body'
                    }`}
                  >
                    {s.label}
                  </span>
                ))}
              </CButton>
            </CCol>
            <CCol md="auto" className="ms-auto">
              <CDropdown alignment="end">
                <CDropdownToggle color="secondary" variant="outline">
                  <CIcon icon={cilColumns} className="me-1" />
                  Columns
                </CDropdownToggle>
                <CDropdownMenu className="p-3">
                  {SEGMENT_COLUMNS[segment].map((col) => (
                    <div className="form-check" key={col.key}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`round-col-${col.key}`}
                        checked={!hiddenColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <label className="form-check-label" htmlFor={`round-col-${col.key}`}>
                        {col.label}
                      </label>
                    </div>
                  ))}
                </CDropdownMenu>
              </CDropdown>
            </CCol>
          </CRow>

          {player && (
            <div className="d-flex flex-wrap align-items-center gap-4 border rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center">
                {imgSrc && (
                  <img src={imgSrc} alt="" width={44} height={44} className="rounded-circle me-2" />
                )}
                <div>
                  <div className="fw-semibold">{player.full_name}</div>
                  <div className="text-medium-emphasis small">
                    {player.team_name || '—'} · {player.position_name || '—'}
                  </div>
                </div>
              </div>
              <StatChip
                icon={cilCalendar}
                label="Rounds Played"
                value={summary.rounds_played ?? 0}
              />
              <StatChip
                icon={cilStar}
                label="Fantasy Points"
                value={(summary.total_fantasy_points ?? 0).toLocaleString()}
              />
              <StatChip
                icon={cilChartLine}
                label="Average per Round"
                value={summary.average_per_round ?? 0}
              />
              <StatChip
                icon={cilBadge}
                label="Best Round"
                value={
                  summary.best_round
                    ? `Round ${summary.best_round} · ${summary.best_round_points} pts`
                    : '—'
                }
              />
              <StatChip
                icon={cilDollar}
                label="Current Value"
                value={`$${Number(summary.current_value ?? 0).toFixed(2)}m`}
              />
            </div>
          )}

          {player && (
            <div className="text-medium-emphasis small mb-2">
              Each row combines all fixtures played by this player within the selected round.
            </div>
          )}

          <div className="table-responsive">
            <CSmartTable
              columns={columns}
              scopedColumns={{
                round: (item) => <td>{item.round}</td>,
                fixtures_count: (item) => <td>{item.fixtures_count}</td>,
                sum_fp: (item) => <td>{item.sum_fp}</td>,
                sum_rs: (item) => <td>{item?.sum_rs}</td>,
                sum_fours: (item) => <td>{item?.sum_fours}</td>,
                sum_sixes: (item) => <td>{item?.sum_sixes}</td>,
                sum_overs: (item) => <td>{item?.sum_overs}</td>,
                sum_mdns: (item) => <td>{item?.sum_mdns}</td>,
                sum_wks: (item) => <td>{item?.sum_wks}</td>,
                sum_cs: (item) => <td>{item?.sum_cs}</td>,
                sum_cwks: (item) => <td>{item?.sum_cwks}</td>,
                sum_sts: (item) => <td>{item?.sum_sts}</td>,
                run_outs: (item) => <td>{(item?.sum_rods || 0) + (item?.sum_roas || 0)}</td>,
                value_at_round: (item) => <td>${Number(item?.value_at_round ?? 0).toFixed(2)}m</td>,
              }}
              items={rows}
              loading={loading}
              noItemsLabel={player ? 'No record yet available.' : 'Select a player to begin.'}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
            />
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

const StatChip = ({ icon, label, value }) => (
  <div className="d-flex align-items-center gap-2">
    <CIcon icon={icon} className="text-medium-emphasis" />
    <div>
      <div className="text-medium-emphasis small">{label}</div>
      <div className="fw-semibold">{value}</div>
    </div>
  </div>
)

export default RoundPlayerStatsTab
